<template>
    <div class="flex flex-col gap-4">
        <!-- Сканер QR-кодов -->
        <div class="bg-white rounded-lg border p-4 cursor-pointer sticky top-0 z-10" @click="focusScanner">
            <div class="flex items-center justify-center">
                <h5 class="font-medium" :class="{
                    'text-blue-600': !errorMessage && scannerFocused,
                    'text-red-500': !scannerFocused || errorMessage
                }">
                    <span v-if="errorMessage">{{ errorMessage }}</span>
                    <span v-else-if="scannerFocused">Отсканируйте QR-код продукции</span>
                    <span v-else>Нажмите для сканирования</span>
                </h5>
            </div>

            <QRTextInput ref="qrScanner" v-model="scannedQR" @scan-complete="handleQRScanned"
                @focus="handleScannerFocus" @blur="scannerFocused = false" placeholder="Отсканируйте QR-код продукции"
                class="hidden-input" :auto-clear="false" />
        </div>


        <!-- Прогресс -->
        <div class="bg-white rounded-lg border p-4" @click="focusScanner">
            <div class="flex justify-between items-center mb-2">
                <h5 class="font-medium text-neutral-900">Прогресс комплектования:</h5>
                <span class="text-sm text-neutral-600">
                    <template v-if="pendingCompletedCount > 0">
                        {{ savedCompletedCount }}(+{{ pendingCompletedCount }})/{{ totalCount }} позиций
                    </template>
                    <template v-else>
                        {{ completedCount }}/{{ totalCount }} позиций
                    </template>
                </span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2 flex">
                <div class="bg-blue-600 h-2 rounded-l-full transition-all duration-300"
                    :style="{ width: savedProgressPercentage + '%' }"
                    :class="{ 'rounded-r-full': pendingProgressPercentage === 0 }">
                </div>
                <div v-if="pendingProgressPercentage > 0" class="bg-orange-500 h-2 transition-all duration-300"
                    :style="{ width: pendingProgressPercentage + '%' }"
                    :class="{ 'rounded-l-full': savedProgressPercentage === 0, 'rounded-r-full': true }">
                </div>
            </div>
        </div>

        <div v-if="pickingInstructions.length === 0" class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p class="text-yellow-800">Нет компонентов для комплектования в данной операции.</p>
        </div>

        <!-- Таблица компонентов для комплектования -->
        <div v-else class="bg-white rounded-lg border overflow-hidden" @click="focusScanner">
            <table class="w-full">
                <thead class="bg-neutral-50">
                    <tr>
                        <th class="px-4 py-2 text-left text-sm font-medium text-neutral-700">Продукция</th>
                        <!-- <th class="px-4 py-2 text-left text-sm font-medium text-neutral-700 text-nowrap">Кол-во</th> -->
                        <th class="px-4 py-2 text-left text-sm font-medium text-neutral-700 text-nowrap">Кол-во</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="instruction in pickingInstructions" :key="getInstructionKey(instruction)">
                        <tr :class="{ 'bg-green-50': getPickedCount(instruction) >= instruction.required_quantity }">
                            <td class="px-4 py-2 text-sm text-neutral-900">
                                {{ instruction.product_name }}
                            </td>
                            <td class="px-4 py-2 text-sm text-neutral-900">
                                <span>{{ getPickedCount(instruction) }}/{{ instruction.required_quantity }}</span>
                                <span v-if="hasOverpick(instruction)"
                                    class="ml-2 px-2 py-0.5 text-xs font-medium text-orange-800 bg-orange-100 border border-orange-300 rounded whitespace-nowrap">
                                    Перерасход
                                </span>
                            </td>
                        </tr>
                        <tr :class="{ 'bg-green-50': getPickedCount(instruction) >= instruction.required_quantity }" class="border-b border-neutral-200">
                            <td colspan="2" class="px-4 py-2 text-sm text-neutral-700">
                                <div v-if="loading && (!instruction.locations || instruction.locations.length === 0) && (!instruction.picked_items || instruction.picked_items.length === 0)"
                                    class="text-gray-500 italic">
                                    Загрузка...
                                </div>
                                <div v-else-if="(instruction.locations && instruction.locations.length > 0) || (instruction.picked_items && instruction.picked_items.length > 0)"
                                    class="space-y-2">
                                    <div v-for="location in instruction.locations" :key="location.cell_id"
                                        class="text-sm p-2 border border-gray-200 rounded bg-gray-50">
                                        <div class="flex justify-between items-start">
                                            <div class="flex-1">
                                                <div class="font-medium">{{ location.cell_address }} - {{ formatQuantity(location.quantity) }} {{ getUnitLabel(instruction) }}</div>
                                                <div class="text-gray-600 text-xs mt-1">{{ formatLocationInfo(location, instruction) }}</div>
                                                <div v-if="location.manufacture_date || location.expiry_date" class="text-gray-600 text-xs mt-1">
                                                    <span v-if="location.manufacture_date">Произведено: {{ formatDate(location.manufacture_date) }}</span>
                                                    <span v-if="location.manufacture_date && location.expiry_date"> | </span>
                                                    <span v-if="location.expiry_date">Годен до: {{ formatDate(location.expiry_date) }}</span>
                                                </div>
                                            </div>
                                            <div class="ml-2 flex">
                                                <input 
                                                    type="checkbox" 
                                                    :checked="!canManualPick(instruction, location) || location.is_picked"
                                                    :id="`manual-pick-${instruction.product_id}-${location.cell_id}`"
                                                    @change="handleManualPick(instruction, location, $event) "
                                                    :disabled="!canManualPick(instruction, location) || location.is_picked"
                                                    class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                                />
                                                <label 
                                                    :for="`manual-pick-${instruction.product_id}-${location.cell_id}`"
                                                    class="ml-1 text-xs text-gray-600 cursor-pointer"
                                                >
                                                    Взял
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <template v-for="(item, index) in instruction.picked_items" :key="`picked-${index}-${item.storehouse_product_id}`">
                                        <div v-if="item.is_single" class="text-sm text-green-700 bg-green-50 p-2 rounded border border-green-200">
                                            ✓ {{ index + 1 }}. {{  getPickedInfo(item) }} {{ item.quantity > 1 ? ' - ' + formatQuantity(item.quantity) + ' ' + getUnitLabel(instruction) : ''}}
                                            <span v-if="item.is_overpick" class="text-orange-700">(перерасход)</span>
                                        </div>
                                    </template>
                                </div>
                                <div v-else class="text-gray-500 italic">
                                    Нет остатков на складе комплектования
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <!-- Модальное окно подтверждения количества -->
        <PickingModal v-if="showModal" :product="modalProduct" :available-quantity="modalAvailableQuantity"
            :is-already-enough="modalIsAlreadyEnough" 
            @confirm="confirmPicking"
            @close="closeModal" />

        <!-- Модальное окно выбора места для количественной продукции -->
        <div v-if="showPlacementModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
                <h2 class="text-lg font-bold mb-4">Выбор места размещения</h2>
                <p class="text-sm text-yellow-800 mb-4 bg-yellow-50 border border-yellow-200 rounded p-3">
                    ⚠️ Система не может определить какую именно продукцию вы отсканировали. Выберите место размещения:
                </p>
                <div class="mb-4 space-y-2 max-h-60 overflow-y-auto">
                    <label v-for="placement in placementModalPlacements" :key="placement.storehouse_product_id"
                        class="flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50"
                        :class="{ 'border-blue-500 bg-blue-50': selectedPlacementId === placement.storehouse_product_id }">
                        <input type="radio" v-model="selectedPlacementId" :value="placement.storehouse_product_id"
                            class="mt-1 mr-3" />
                        <div class="flex-1">
                            <div class="font-medium">{{ placement.full_address }}</div>
                            <div class="text-sm text-gray-600">{{ formatQuantity(placement.count) }} {{ getUnitLabel(placementModalInstruction) }}</div>
                        </div>
                    </label>
                </div>
                <div class="flex justify-end space-x-2">
                    <button @click="closePlacementModal" 
                        class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">
                        Пересканировать
                    </button>
                    <button @click="confirmPlacementSelection" :disabled="!selectedPlacementId"
                        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        Готово
                    </button>
                </div>
            </div>
        </div>

        <!-- Подтверждение перерасхода по ответу склада: часть позиций не сохранена -->
        <div v-if="showOverpickModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
                <h2 class="text-lg font-bold mb-4">Больше нормы взять нельзя</h2>
                <p class="text-sm text-neutral-700 mb-3">
                    Комплектование идёт строго по заданию. Эти позиции превышают норму и не сохранены:
                </p>
                <div class="mb-4 space-y-2 max-h-60 overflow-y-auto">
                    <div v-for="(row, index) in overpickRows" :key="`overpick-${index}-${row.uid || row.storehouse_product_id}`"
                        class="p-3 bg-red-50 border border-red-300 rounded">
                        <div class="font-medium text-sm text-red-900">{{ row.product_name }}</div>
                        <div class="text-sm text-red-800 mt-1">{{ row.message }}</div>
                    </div>
                </div>
                <div class="flex justify-end">
                    <button @click="cancelOverpick"
                        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
                        Понятно
                    </button>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import QRTextInput from '@/components/QRTextInput.vue'
import PickingModal from './PickingModal.vue'
import axios from 'axios'

export default {
    name: 'PickingComponents',
    components: {
        QRTextInput,
        PickingModal
    },
    props: {
        operationId: {
            type: Number,
            required: true
        },
        workshopPostUnitId: {
            type: Number,
            required: true
        },
        components: {
            type: Array,
            required: true
        },
        savedProgress: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            pickingInstructions: [],
            scannedQR: '',
            manualPid: '',
            scannerFocused: false,
            showModal: false,
            modalProduct: null,
            modalInstruction: null,
            modalAvailableQuantity: 1,
            modalIsAlreadyEnough: false,
            // Окно подтверждения перерасхода по ответу склада
            showOverpickModal: false,
            overpickRows: [],
            overpickResolve: null,
            errorMessage: '',
            loading: false,
            showPlacementModal: false,
            placementModalProduct: null,
            placementModalInstruction: null,
            placementModalPlacements: [],
            placementModalParsedData: null,
            selectedPlacementId: null,
            pendingItems: [],
            pendingUidCounter: 0,
            savingProgress: false
        }
    },
    computed: {
        completedCount() {
            return this.pickingInstructions.filter(instruction =>
                this.getPickedCount(instruction) >= instruction.required_quantity
            ).length
        },
        savedCompletedCount() {
            return this.pickingInstructions.filter(instruction =>
                this.getSavedPickedCount(instruction) >= instruction.required_quantity
            ).length
        },
        pendingCompletedCount() {
            return this.completedCount - this.savedCompletedCount
        },
        totalCount() {
            return this.pickingInstructions.length
        },
        savedProgressPercentage() {
            if (this.totalCount === 0) return 0
            return Math.round((this.savedCompletedCount / this.totalCount) * 100)
        },
        pendingProgressPercentage() {
            if (this.totalCount === 0) return 0
            return Math.round((this.pendingCompletedCount / this.totalCount) * 100)
        },
        progressPercentage() {
            if (this.totalCount === 0) return 0
            return Math.round((this.completedCount / this.totalCount) * 100)
        },
        hasPendingItems() {
            return this.pendingItems.length > 0
        }
    },
    mounted() {
        // Инструкции либо восстанавливаются из прогресса родителя, либо грузятся с бэка —
        // обе ветки внутри loadFIFOLocations
        this.loadFIFOLocations()

        this.$nextTick(() => {
            this.focusScanner()
        })
    },
    watch: {
        // Единый источник признака несохранённых позиций для родителя.
        // Компонент размонтируется при переходе на другой шаг, поэтому признак
        // должен выставляться от реальной очереди, а не отдельными вызовами
        hasPendingItems: {
            handler(hasPending) {
                this.$emit('has-pending', hasPending)
            },
            immediate: true
        }
    },
    methods: {
        formatQuantity(val) {
            if (val == null || val === undefined) return '0';
            const num = Number(val);
            return isNaN(num) ? '0' : (Math.round(num * 1000) / 1000).toString();
        },
        getUnitLabel(product) {
            return product?.unit_short_name || product?.unit?.short_name || 'шт';
        },
        handleManualPidSubmit() {
            if (!this.manualPid || this.manualPid.trim() === '') return
            
            // Формируем данные в формате pid=X и вызываем handleQRScanned
            const pidData = `pid=${this.manualPid.trim()}`
            this.handleQRScanned(pidData)
            
            // Очищаем поле после отправки
            this.manualPid = ''
        },

        initializeInstructions() {
            // Создаем базовые инструкции из переданных компонентов
            this.pickingInstructions = this.components.map(component => ({
                product_id: component.product_id,
                product_name: component.product.name,
                operation_step_component_id: component.id ?? null,
                required_quantity: component.count,
                picked_quantity: 0,
                status: 'pending',
                locations: [],
                total_available: 0
            }))

            // console.log('Инициализированы инструкции комплектования:', this.pickingInstructions)
        },

        // Идентификатор позиции в очереди. По нему бэкенд отсекает повторную отправку
        // после обрыва связи: перемещение комплектующих со склада отменить нельзя
        createPendingUid() {
            this.pendingUidCounter += 1

            return `${this.operationId}-${Date.now()}-${this.pendingUidCounter}`
        },

        // Собираем очередь на отправку заново из отметок с флагом pending.
        // Нужно после возврата на шаг комплектования: инструкции с отметками
        // хранятся у родителя, а очередь живёт в компоненте и теряется при размонтировании
        restorePendingItems() {
            const pending = []

            for (const instruction of this.pickingInstructions) {
                for (const item of instruction.picked_items || []) {
                    if (!item.pending) continue

                    pending.push({
                        uid: item.pending_uid,
                        storehouse_product_id: item.storehouse_product_id,
                        quantity: item.quantity || 1,
                        product_id: instruction.product_id,
                        // Компонент шага: по нему склад считает потребность и перерасход
                        operation_step_component_id: instruction.operation_step_component_id ?? null,
                    })
                }
            }

            this.pendingItems = pending
        },

        // Строка инструкции принадлежит компоненту конкретного шага: один продукт
        // может встречаться в нескольких шагах одной операции
        getInstructionKey(instruction) {
            return instruction.operation_step_component_id
                ? `component-${instruction.operation_step_component_id}`
                : `product-${instruction.product_id}`
        },

        // Из нескольких строк одного продукта берём ту, потребность которой ещё не закрыта
        selectInstruction(candidates) {
            if (!candidates || candidates.length === 0) return null

            return candidates.find(
                instruction => this.getPickedCount(instruction) < Number(instruction.required_quantity)
            ) || candidates[0]
        },

        // Хотя бы одно взятие по строке сохранено как перерасход
        hasOverpick(instruction) {
            if (instruction.has_overpick) return true

            return (instruction.picked_items || []).some(item => item.is_overpick === true)
        },

        async loadFIFOLocations() {

            if (this.savedProgress.length > 0) {
                this.pickingInstructions = this.savedProgress
                this.restorePendingItems()
                return
            }

            try {
                this.loading = true

                const response = await axios.post('/api/picking/instructions', {
                    operation_id: this.operationId,
                    module_id: this.workshopPostUnitId
                })

                if (response.data.success) {
                    console.log('FIFO инструкции:', response.data.data)
                    const fifoData = response.data.data || []

                    // Используем данные из API напрямую (там уже есть picked_items, picked_quantity)
                    this.pickingInstructions = fifoData.map(instruction => ({
                        ...instruction,
                        status: 'pending',
                        locations: instruction.locations || [],
                        total_available: instruction.total_available || 0,
                        picked_quantity: instruction.picked_quantity || 0,
                        picked_items: instruction.picked_items || []
                    }))
                    
                    console.log('Загруженные инструкции:', this.pickingInstructions);
                    
                } else {
                    console.warn('Не удалось загрузить FIFO локации:', response.data.message)
                    // Создаем базовые инструкции из компонентов
                    this.initializeInstructions()
                }
            } catch (error) {
                console.error('Ошибка при загрузке FIFO локаций:', error)
                // Создаем базовые инструкции из компонентов
                this.initializeInstructions()
            } finally {
                this.loading = false
            }
        },

        async handleQRScanned(scannedData) {
            if (!scannedData || scannedData.trim() === '') return

            try {
                // Парсим QR-код
                const parsedData = this.parseQRData(scannedData)

                // Проверяем что это QR-код продукции
                if (!this.isProductQR(parsedData)) {
                    this.showError('Отсканированный QR-код не является кодом продукции')
                    this.autoHideError()
                    this.clearScannedQR()
                    return
                }

                // Вариант 1: Инвентарная этикетка (spid)
                if (parsedData.spid) {
                    await this.handleInventorySticker(parsedData.spid)
                    return
                }

                // Вариант 2: Этикетка входного контроля (pid/bpid)
                if (parsedData.pid || parsedData.bpid) {
                    await this.handleInputControlSticker(parsedData)
                    return
                }

                this.showError('Неопознанный формат QR-кода')
                this.autoHideError()
                this.clearScannedQR()
            } catch (error) {
                console.error('Ошибка при валидации QR-кода:', error)
                this.showError(error.response?.data?.message || 'Ошибка при валидации отсканированной продукции')
                this.autoHideError()
                this.clearScannedQR()
            }
        },

        async handleInventorySticker(spid) {
            const storehouseProductId = parseInt(spid)
            
            // Ищем в инструкциях. Один продукт может стоять в нескольких шагах операции —
            // берём строку с незакрытой потребностью
            const candidates = this.pickingInstructions.filter(instruction =>
                (instruction.locations || []).some(loc => loc.storehouse_product_id === storehouseProductId)
            )
            let foundInstruction = this.selectInstruction(candidates)
            const foundLocation = foundInstruction
                ? foundInstruction.locations.find(loc => loc.storehouse_product_id === storehouseProductId)
                : null

            // Делаем запрос для получения актуальных данных
            const params = new URLSearchParams({
                spid: storehouseProductId,
                module_id: this.workshopPostUnitId
            }).toString()

            const response = await axios.get(`/api/tsdScan/scanProductByBpid?${params}`)

            if (!response.data.success || !response.data.data) {
                this.showError('Не удалось получить данные о продукции')
                this.autoHideError()
                this.clearScannedQR()
                return
            }

            const product = response.data.data

            // Потребность закрыта — мест взятия в инструкции уже нет, но продукция
            // в задании есть: открываем строку и спрашиваем подтверждение перерасхода
            if (!foundInstruction) {
                foundInstruction = this.selectInstruction(
                    this.pickingInstructions.filter(
                        inst => Number(inst.product_id) === Number(product.product_id)
                    )
                )
            }

            if (!foundInstruction) {
                this.showError('Отсканированная продукция не найдена в инструкциях')
                this.autoHideError()
                this.clearScannedQR()
                return
            }

            // Проверяем не взята ли уже (только для штучной)
            if (product.is_single) {
                const alreadyPicked = foundInstruction.picked_items?.some(item =>
                    item.storehouse_product_id === storehouseProductId
                )
                if (alreadyPicked) {
                    this.showError('Продукция уже была отсканирована')
                    this.autoHideError()
                    this.clearScannedQR()
                    return
                }
            }
            const isAlreadyEnough = Number(this.getPickedCount(foundInstruction)) >= Number(foundInstruction.required_quantity)

            // Получаем актуальное количество из placement
            const placement = product.placements?.[0]
            const actualQuantity = placement?.count ?? 0

            // Формируем данные продукта для модалки
            const productData = {
                product_id: foundInstruction.product_id,
                product_name: foundInstruction.product_name,
                storehouse_product_id: storehouseProductId,
                serial_number: product.serial_number,
                batch_info: product.batch_info,
                batch_number: foundLocation?.batch_number,
                supplier_name: foundLocation?.supplier_name,
                receipt_invoice_id: foundLocation?.receipt_invoice_id,
                is_single: product.is_single,
                available_quantity: actualQuantity,
                is_package: placement?.is_package ?? false,
                placements: product.placements,
                valid_location_ids: (foundInstruction.locations || []).map(loc => loc.storehouse_product_id),
                unit_short_name: foundInstruction.unit_short_name,
                required_quantity: foundInstruction.required_quantity,
                picked_count: this.getPickedCount(foundInstruction)
            }

            this.showPickingModal(productData, { spid }, isAlreadyEnough, foundLocation, foundInstruction)
        },

        async handleInputControlSticker(parsedData) {
            // Используем scanProductByBpid из ТСД
            const bpid = parsedData.bpid || parsedData.pid
            const params = new URLSearchParams({
                bpid: bpid,
                module_id: this.workshopPostUnitId
            }).toString()

            const response = await axios.get(`/api/tsdScan/scanProductByBpid?${params}`)

            if (!response.data.success || !response.data.data) {
                this.showError('Не удалось получить данные о продукции')
                this.autoHideError()
                this.clearScannedQR()
                return
            }

            const product = response.data.data
            const productId = product.product_id

            // Проверяем что продукция нужна для инструкций. Продукт может стоять
            // в нескольких шагах операции — берём строку с незакрытой потребностью
            const instruction = this.selectInstruction(
                this.pickingInstructions.filter(inst => Number(inst.product_id) === Number(productId))
            )

            if (!instruction) {
                this.showError('Отсканирована не подходящая продукция')
                this.autoHideError()
                this.clearScannedQR()
                return
            }

            const isSingle = product.is_single
            const placements = product.placements || []

            // Проверяем не взята ли уже (только для штучной)
            if (isSingle) {
                const alreadyPicked = instruction.picked_items?.some(item => 
                    item.batch_product_id === product.batch_product_id
                )
                if (alreadyPicked) {
                    this.showError('Продукция уже была отсканирована')
                    this.autoHideError()
                    this.clearScannedQR()
                    return
                }
            }

            // 2.1 Если штучная - получаем конкретный storehouse_product_id
            if (isSingle) {
                if (placements.length === 0) {
                    this.showError('Продукция не размещена на складе')
                    this.autoHideError()
                    this.clearScannedQR()
                    return
                }

                const placement = placements[0]
                const storehouseProductId = placement.storehouse_product_id

                // Проверяем что есть в инструкциях. Пустой список мест означает закрытую
                // потребность — взятие сверх плана подтверждается в окне взятия
                const locations = instruction.locations || []
                const location = locations.find(loc =>
                    loc.storehouse_product_id === storehouseProductId
                )

                if (!location && locations.length > 0) {
                    this.showError('Отсканированная продукция не найдена в инструкциях')
                    this.autoHideError()
                    this.clearScannedQR()
                    return
                }

                const isAlreadyEnough = Number(this.getPickedCount(instruction)) >= Number(instruction.required_quantity)

                const productData = {
                    ...product,
                    storehouse_product_id: storehouseProductId,
                    unit_short_name: product.unit_short_name || instruction.unit_short_name,
                    required_quantity: instruction.required_quantity,
                    picked_count: this.getPickedCount(instruction)
                }

                this.showPickingModal(productData, parsedData, isAlreadyEnough, location, instruction)
                return
            }

            // 2.2 Если количественная - показываем ВСЕ места
            if (placements.length === 0) {
                this.showError('Продукция не размещена на складе')
                this.autoHideError()
                this.clearScannedQR()
                return
            }

            const isAlreadyEnough = Number(this.getPickedCount(instruction)) >= Number(instruction.required_quantity)

            // Получаем список валидных storehouse_product_id из инструкций
            const validLocationIds = (instruction.locations || []).map(loc => loc.storehouse_product_id)

            const productData = {
                ...product,
                placements: placements,
                available_quantity: placements[0].count,
                valid_location_ids: validLocationIds,
                unit_short_name: product.unit_short_name || instruction.unit_short_name,
                required_quantity: instruction.required_quantity,
                picked_count: this.getPickedCount(instruction)
            }

            this.showPickingModal(productData, parsedData, isAlreadyEnough, null, instruction)
        },

        clearScannedQR() {
            this.scannedQR = ''
            this.$nextTick(() => {
                this.focusScanner()
            })
        },

        showPickingModal(product, parsedData, isAlreadyEnough = false, location = null, instruction = null) {
            // Строку инструкции запоминаем: искать её потом по product_id нельзя —
            // один продукт может стоять в нескольких шагах операции
            this.modalInstruction = instruction
            this.modalProduct = {
                ...product,
                storehouse_product_id: product.storehouse_product_id || product.storehouse_info?.storehouse_product_id,
                pid: parsedData.pid || parsedData.bpid || parsedData.spid,
                serial_number: product.serial_number || parsedData.s || null,
                available_quantity: product.available_quantity,
                is_single: product.is_single
            }
            this.modalAvailableQuantity = product.available_quantity
            this.modalIsAlreadyEnough = isAlreadyEnough
            this.showModal = true
        },

        confirmPicking(result) {
            // Обрабатываем как объект или число (для обратной совместимости)
            const quantity = typeof result === 'object' ? result.quantity : result
            const storehouseProductId = typeof result === 'object' && result.storehouse_product_id
                ? result.storehouse_product_id
                : this.modalProduct.storehouse_product_id

            const instruction = this.modalInstruction || this.pickingInstructions.find(
                inst => inst.product_id === this.modalProduct.product_id
            )

            if (!instruction) {
                this.showError('Инструкция не найдена')
                this.autoHideError()
                return
            }

            // Добавляем в pending (для батч-отправки)
            const pendingUid = this.createPendingUid()

            this.pendingItems.push({
                uid: pendingUid,
                storehouse_product_id: storehouseProductId,
                quantity: quantity,
                product_id: this.modalProduct.product_id,
                operation_step_component_id: instruction.operation_step_component_id ?? null,
            })

            // Локально обновляем picked_items с флагом pending
            if (!instruction.picked_items) {
                instruction.picked_items = []
            }
            instruction.picked_items.push({
                pending_uid: pendingUid,
                product_id: this.modalProduct.product_id,
                serial_number: this.modalProduct.serial_number,
                batch_product_id: this.modalProduct.batch_product_id,
                batch_number: this.modalProduct.batch_number || this.modalProduct.batch_info?.batch_number,
                supplier_name: this.modalProduct.supplier_name,
                storehouse_product_id: storehouseProductId,
                receipt_invoice_id: this.modalProduct.receipt_invoice_id,
                quantity: quantity,
                is_single: this.modalProduct.is_single,
                is_overpick: false,
                pending: true
            })

            // Уменьшаем количество в локации для реактивности
            const location = instruction.locations.find(loc =>
                loc.storehouse_product_id === storehouseProductId
            )
            if (location) {
                // Место предлагает столько, сколько осталось по потребности. Взяли сверх плана —
                // предлагать больше нечего, но и отрицательное количество показывать нельзя
                location.quantity = Math.max(0, location.quantity - quantity)
                if (location.quantity <= 0) {
                    location.is_picked = true
                }
            }

            this.$emit('picked-items-data', this.getPickedItemsData())

            this.closeModal()
        },

        closeModal() {
            this.showModal = false
            this.modalProduct = null
            this.modalInstruction = null
            this.modalAvailableQuantity = 1
            this.modalIsAlreadyEnough = false

            // Возвращаем фокус на сканер при любом закрытии модального окна
            this.$nextTick(() => {
                this.focusScanner()
            })
        },



        focusScanner() {
            this.$nextTick(() => {
                if (this.$refs.qrScanner) {
                    this.$refs.qrScanner.focus()
                    this.scannerFocused = true
                }
            })
        },

        handleScannerFocus() {
            this.scannerFocused = true
            // Очищаем ошибку при получении фокуса
            if (this.errorMessage) {
                this.clearError()
            }
        },

        showError(message, type = 'error') {
            this.errorMessage = message
            // Автоскрытие только для обычных ошибок
            if (type === 'error') {
                this.autoHideError()
            }
        },

        autoHideError() {
            setTimeout(() => {
                this.clearError()
            }, 3000)
        },

        clearError() {
            this.errorMessage = ''
        },

        formatDate(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`
        },

        // Методы парсинга QR-кода (скопированы из TsdReceptionMode.vue)
        parseQRData(data) {
            const params = new URLSearchParams(data)
            const result = {}
            for (const [key, value] of params) {
                result[key] = value
            }
            return result
        },

        isProductQR(parsedData) {
            // Инвентарные этикетки (spid) или этикетки входного контроля (pid/bpid)
            return parsedData && (parsedData.pid || parsedData.bpid || parsedData.spid)
        },

        // Метод для получения данных отсканированных позиций для списания
        getPickedItemsData() {            
            // console.log('Собранные данные для списания:', this.pickingInstructions)
            return this.pickingInstructions
        },

        formatLocationInfo(location, instruction) {
            const parts = []

            // Упаковка (только для количественной)
            if (location.is_package && !location.is_single) {
                parts.push(`Упаковка №${location.storehouse_product_id}`)
            }

            // Партия
            if (location.batch_number) {
                parts.push(`Партия: ${location.batch_number}`)
            }

            // Производитель
            if (location.supplier_name) {
                parts.push(`Поставщик: ${location.supplier_name}`)
            }

            // Серийный номер (только если есть)
            if (location.serial_number) {
                parts.push(`SN: ${location.serial_number}`)
            }

            // Если нет партии - показываем накладную
            if (!location.batch_number && location.receipt_invoice_id) {
                parts.push(`Накладная: ${location.receipt_invoice_id}`)
            }

            return parts.join(' | ')
        },

        getPickedInfo(item) {
            const parts = []

            // Партия
            if (item.batch_number) {
                parts.push(`Партия: ${item.batch_number}`)
            }

            // Производитель
            if (item.supplier_name) {
                parts.push(`Поставщик: ${item.supplier_name}`)
            }

            // Серийный номер (только если есть)
            if (item.serial_number) {
                parts.push(`SN: ${item.serial_number}`)
            }

            // Если нет партии - показываем накладную
            if (!item.batch_number && item.receipt_invoice_id) {
                parts.push(`Накладная: ${item.receipt_invoice_id}`)
            }

            return parts.join(' | ')
        },

        // Проверяем можно ли вручную взять с этой локации
        canManualPick(instruction, location) {
            // Нельзя если уже набрали достаточно
            if (this.getPickedCount(instruction) >= instruction.required_quantity) {
                return false
            }

            // Нельзя если с этой локации уже взяли (проверяем по storehouse_product_id)
            if (instruction.picked_items && instruction.picked_items.length > 0) {
                const alreadyPicked = instruction.picked_items.some(item => 
                    item.storehouse_product_id === location.storehouse_product_id
                )
                if (alreadyPicked) return false
            }

            return location.available_quantity > 0
        },

        // Обработка ручного взятия комплектующей
        async handleManualPick(instruction, location, event) {
            if (!event.target.checked) return

            try {
                // Снимаем галочку сразу, чтобы предотвратить повторные клики
                event.target.checked = false

                // Вычисляем сколько еще надо взять
                const remaining = instruction.required_quantity - this.getPickedCount(instruction)
                const availableInLocation = location.quantity || 0
                const quantityToPick = Math.min(remaining, availableInLocation)

                if (quantityToPick <= 0) {
                    this.showError('Достаточное количество уже взято')
                    return
                }

                // Создаем объект продукта на основе локации
                const pickedItem = {
                    product_id: instruction.product_id,
                    serial_number: location.serial_number || null,
                    batch_product_id: location.batch_product_id || null,
                    batch_number: location.batch_number || null,
                    batch_id: location.batch_id || null,
                    cell_id: location.cell_id,
                    storehouse_product_id: location.storehouse_product_id || null,
                    receipt_invoice_id: location.receipt_invoice_id || null,
                    manufacture_date: location.manufacture_date || null,
                    expiry_date: location.expiry_date || null,
                    quantity: quantityToPick, // Добавляем количество
                    manual_pick: true
                }

                // Отправляем запрос на бэкенд для списания
                const requestData = {
                    picked_item: pickedItem,
                    module_id: this.workshopPostUnitId,
                    operation_id: this.operationId
                }
                
                // Добавляем в pending (для батч-отправки)
                const pendingUid = this.createPendingUid()

                this.pendingItems.push({
                    uid: pendingUid,
                    storehouse_product_id: pickedItem.storehouse_product_id,
                    quantity: quantityToPick,
                    product_id: instruction.product_id,
                    operation_step_component_id: instruction.operation_step_component_id ?? null,
                    // Вручную берут только в пределах потребности — перерасхода здесь нет
                })

                // Локально обновляем picked_items с флагом pending
                if (!instruction.picked_items) {
                    instruction.picked_items = []
                }
                instruction.picked_items.push({
                    ...pickedItem,
                    quantity: quantityToPick,
                    pending: true,
                    pending_uid: pendingUid
                })

                // Уменьшаем доступное количество в локации (для UI)
                location.available_quantity -= quantityToPick

                // Передаем данные наверх
                this.$emit('picked-items-data', this.getPickedItemsData())

                this.showSuccess(`Взято ${this.formatQuantity(quantityToPick)} ${this.getUnitLabel(instruction)}`)
            } catch (error) {
                console.error('Ошибка при ручном взятии:', error)
                this.showError(error.response?.data?.message || 'Ошибка при ручном взятии комплектующей')
                // Восстанавливаем галочку при ошибке
                event.target.checked = false
            }
        },

        // Генерируем mock batch_product_id для ручного взятия
        generateMockBatchProductId(location) {
            // Создаем уникальный ID на основе данных локации
            return `manual_${location.cell_id}_${location.batch_id || location.receipt_invoice_id}_${Date.now()}`
        },

        showSuccess(message) {
            // Можно добавить toast уведомление или просто консоль
            console.log('Успех:', message)
        },

        getPickedCount(instruction) {
            // Считаем только по picked_items, не добавляем picked_quantity (это двойной счёт)
            const raw = instruction.picked_items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0;
            return Math.round(raw * 1000) / 1000;
        },

        getSavedPickedCount(instruction) {
            // Считаем только сохранённые (не pending) элементы
            const raw = instruction.picked_items?.filter(item => !item.pending)
                .reduce((sum, item) => sum + (item.quantity || 1), 0) || 0;
            return Math.round(raw * 1000) / 1000;
        },

        async savePendingProgress() {
            // Сохранять нечего — это успех, а не отказ: иначе завершение операции
            // блокируется молча
            if (this.pendingItems.length === 0) return true
            if (this.savingProgress) return false

            this.savingProgress = true
            try {
                return await this.sendPendingItems(this.pendingItems.slice())
            } finally {
                this.savingProgress = false
            }
        },

        // Отправка очереди на склад. Позиции с неподтверждённым перерасходом склад не сохраняет
        // и возвращает отдельным списком — по ним показываем, что норма превышена.
        // Повторной отправки нет: перерасход запрещён строго (Z-21)
        async sendPendingItems(items) {
            if (items.length === 0) return true

            try {
                const response = await axios.post('/api/picking/complete-batch', {
                    module_id: this.workshopPostUnitId,
                    operation_id: this.operationId,
                    items: items.map(item => ({
                        uid: item.uid,
                        storehouse_product_id: item.storehouse_product_id,
                        quantity: item.quantity,
                        operation_step_component_id: item.operation_step_component_id ?? null,
                    }))
                })

                const overpickRequired = response.data?.overpick_required || []
                const rejectedUids = overpickRequired.map(row => row.uid)

                // Сохранённые позиции снимаем с очереди сразу, даже если часть отклонена
                this.markItemsSaved(items.filter(item => !rejectedUids.includes(item.uid)))

                if (overpickRequired.length === 0) return true

                // Норма превышена: показываем оператору, по каким позициям и насколько,
                // и оставляем их в очереди — он уменьшает количество и сохраняет заново
                await this.showOverpickRefusal(overpickRequired)
                this.showSaveError('Позиции сверх нормы не сохранены. Уменьшите количество до нормы по заданию')

                return false
            } catch (error) {
                console.error('Ошибка при сохранении прогресса:', error)
                this.showSaveError(error.response?.data?.message)
                return false
            }
        },

        // Снимаем флаг «не сохранено» с принятых складом позиций
        markItemsSaved(savedItems) {
            if (savedItems.length === 0) return

            const savedUids = savedItems.map(item => item.uid)

            for (const instruction of this.pickingInstructions) {
                for (const item of instruction.picked_items || []) {
                    if (item.pending && savedUids.includes(item.pending_uid)) {
                        delete item.pending
                        delete item.pending_uid
                    }
                }
            }

            this.pendingItems = this.pendingItems.filter(item => !savedUids.includes(item.uid))
            this.$emit('picked-items-data', this.getPickedItemsData())
        },

        // Пометка перерасхода на отметках рабочего места — чтобы она была видна сразу
        // Ждём решения комплектовщика по окну подтверждения перерасхода
        // Показывает окно с позициями, превысившими норму, и ждёт, пока оператор его закроет
        showOverpickRefusal(rows) {
            this.overpickRows = rows
            this.showOverpickModal = true

            return new Promise(resolve => {
                this.overpickResolve = resolve
            })
        },

        // Перерасход запрещён строго (Z-21): окно только сообщает, какие позиции
        // превысили норму, подтвердить их нельзя
        cancelOverpick() {
            this.closeOverpickModal()
        },

        // Ждущий промис разрешается без значения: подтверждать перерасход больше нечем,
        // окно только сообщает о превышении нормы (Z-21)
        closeOverpickModal() {
            this.showOverpickModal = false
            this.overpickRows = []

            const resolve = this.overpickResolve
            this.overpickResolve = null

            this.$nextTick(() => {
                this.focusScanner()
            })

            if (resolve) resolve()
        },

        // Ошибку сохранения показываем тостом: шапка сканера гаснет через 3 секунды,
        // и оператор её не замечает. Отметки при этом остаются несохранёнными,
        // очередь не сбрасывается и кнопка «Сохранить» доступна для повторной попытки
        showSaveError(message) {
            const text = message || 'Не удалось сохранить комплектование. Попробуйте ещё раз'

            // Тост недоступен — сообщение всё равно должно дойти до оператора
            if (this.$root.$toast) {
                this.$root.$toast.error(text)
            } else {
                alert(text)
            }
        }
    },
}
</script>

<style scoped>
.hidden-input {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    opacity: 0 !important;
    pointer-events: none !important;
}
</style>